import React, { useContext, useState } from "react";
import HeaderUpc from "../h1upc";
import TaskNav from "../task-nav/task-nav";
import ShotSelector from "./components/shot-selector";
import ShotLocales from "./components/shot-locales";
import MultiFileUpload from "./components/multifile-upload";
import ImagesLink from "../image-stream/images-link";
import NotificationBar from "../../components/notification-bar";
import TaskMessages from "../../components/notification-bar/task-messages";
import NoteLauncher from "../notes";
import PhotographyShots from "./context/photography";
import { TaskContext } from "../../contexts/task";
import { useViewOnlyPermissions } from "shared/hooks/use-view-only-permissions";
import PhotoQaNav from "./components/photo-qa-nav";
import { RejectToSupplier } from "./components/reject-to-supplier";
import BulkSelect from "./components/bulk-select";
import RemoveEmptySlots from "./components/remove-empty-slots";
import { MeasurementsDetails } from "shared/components/measurements/measuerement-review";
import PhotographyLookups from "../../contexts/photography/photography";
import { SidePanel } from "../side-panel/side-panel";
import { EditMeasurementsModal } from "./components/edit-measurements-modal";
import DuplicateCheck from "components/duplicate-check";
import classnames from "classnames";

import "./photography.scss";
import { useModal } from "../../shared/hooks/use-modal";
import { getWipMeasurements } from "../measuring/lib/measuring-data";
import eventEmitter from "../../shared/events";
import { AttributesContext } from "../../contexts/attributes";
import { lookupUUID } from "../../shared/attribute-uuid";
import { SourcePage } from "../../shared/taskTypeMap";
import { useNavigate, useParams } from "react-router-dom";
import { TaskStatus } from "shared/constants/task-status";
import { useShipmentProductFeatures } from "shared/hooks/use-shipment-product-features";
import { cesFeatureGuids } from "../../contexts/settings/lib/constants";

const PhotographyIndex = () => {
    const { task } = useContext(TaskContext);
    const { data } = useContext(AttributesContext);
    const isUploading = task.state === TaskStatus.UPLOADING;
    const imagesContainer = classnames("row", {
        "disable-photography": isUploading,
    });
    const navigate = useNavigate();
    const params = useParams();
    const taskKey = params.id;
    const wipKey = params.wip;
    const imperialWidth = data[lookupUUID("width")];
    const productNameAttribute = data[lookupUUID("supplierProductName")];
    const { open: isEditMeasurementsModalOpen, onOpen: handleOpenEditMeasurementsModal, onClose: handleCloseEditMeasurementsModal } = useModal();
    const [existingMeasurements, setExistingMeasurements] = useState([]);
    const isDigital = task.context.shipmentType === 1;
    const isDigitalLite = task.context.shipmentType === 2;
    const hasIMDropboxContent =
        task.hasDigitalContent && task.locationInfo.companyName === "ItemMaster" && task.locationInfo.location !== "BYOC_Digital";
    const { featureName: costcoFeatureName } = useShipmentProductFeatures(cesFeatureGuids.COSTCOSPSUP, task.context.shipmentProductKey);
    const { productHasFeature: isManualDigital } = useShipmentProductFeatures(
        cesFeatureGuids.MANDIGITALPRODUCT,
        task.context.shipmentProductKey,
        true
    );

    const isVOUpload = useViewOnlyPermissions("UploadShot", task.sourcePage);
    const isVODelete = useViewOnlyPermissions("DeleteShot", task.sourcePage);
    const isVOSourceEdit = useViewOnlyPermissions("EditSource", task.sourcePage);
    const isShotSelectorDisabled = useViewOnlyPermissions("PackageType", task.sourcePage);
    const isCompleteTaskDisabled = useViewOnlyPermissions("CompleteTask", task.sourcePage);
    const isCreateNoteDisabled = useViewOnlyPermissions("CreateNote", task.sourcePage);

    const onComplete = () => {
        navigate("/task-list/photography");
    };

    const handleEditMeasurements = async () => {
        const measurements = await getWipMeasurements(task.context.measuringWipKey);
        setExistingMeasurements(measurements);

        handleOpenEditMeasurementsModal();
    };

    const handleCloseModal = () => {
        handleCloseEditMeasurementsModal();

        eventEmitter.emit("REFRESH_ATTRIBUTES");
    };

    return (
        <PhotographyLookups
            includeMultipleImageEligibleItems={true}
            taskContextKey={task.taskContextKey}
        >
            <EditMeasurementsModal
                sourcePage={SourcePage["photography.standard"]}
                open={isEditMeasurementsModalOpen}
                onClose={handleCloseModal}
                existingMeasurements={existingMeasurements}
            />
            <div className="flex">
                <SidePanel disableControls={isUploading} />
                <div className="container flex-1">
                    <PhotographyShots
                        photoWipKey={wipKey}
                        taskKey={taskKey}
                        isCompleted={task.isCompleted}
                    >
                        <TaskNav
                            taskKey={taskKey}
                            hasIMDropboxContent={hasIMDropboxContent}
                            isSampleProduct={task.context.isSampleUpc}
                        >
                            <div className="button-group">
                                <HeaderUpc text="Photography" />
                                {!task.isCompleted && (
                                    <RemoveEmptySlots
                                        isReadOnly={isVODelete}
                                        isUploading={isUploading}
                                    />
                                )}
                                {!isManualDigital && (isDigital || isDigitalLite) && (
                                    <RejectToSupplier
                                        wipKey={wipKey}
                                        onComplete={onComplete}
                                        isUploading={isUploading}
                                        workflowType={task.context.workflowTypeId}
                                    />
                                )}
                                {task.context.matchedProductTaskContextKey && <DuplicateCheck matchType={task.context.productMatchType} />}
                                <NoteLauncher
                                    settings={{
                                        shipmentKey: task.context.shipmentKey,
                                        shipmentProductKey: task.context.shipmentProductKey,
                                    }}
                                    clientCode={task.context.cxHClientCode}
                                    binTrackingNumber={[task.context.binTrackingNumber]}
                                    noteType="Product"
                                    taskType="photography.standard"
                                    isReadOnly={isCreateNoteDisabled || isUploading}
                                    sourcePage={task.sourcePage}
                                />
                                <ImagesLink
                                    disabled={isUploading}
                                    shipmentKey={task.context.shipmentKey}
                                    shipmentProductKey={task.context.shipmentProductKey}
                                />
                                {!task.isCompleted && (
                                    <PhotoQaNav
                                        taskKey={taskKey}
                                        onComplete={onComplete}
                                        showEcom={!task.context.isSampleUpc}
                                        isQA={false}
                                        isReadOnly={isCompleteTaskDisabled || isUploading}
                                    />
                                )}
                            </div>
                        </TaskNav>
                        {task.context.hasPremiumEditing && <NotificationBar message="This product has been flagged for premium editing." />}
                        {task.isBrandVerified && <NotificationBar message="Brand Verified" />}
                        <TaskMessages message={task.messages ? task.messages.photography : null} />
                        {isUploading && (
                            <div className="row">
                                <div className="column -span3" />
                                <div className="column -span9">
                                    <NotificationBar type="error">
                                        <div>
                                            A bulk upload is currently being processed for this UPC. No changes can be made to this UPC until the bulk
                                            upload process is complete.
                                        </div>
                                    </NotificationBar>
                                </div>
                            </div>
                        )}

                        <div className={imagesContainer}>
                            <div className="column -span3 l-sticky-panel">
                                <ShotSelector
                                    costcoFeatureName={costcoFeatureName}
                                    productNameAttribute={productNameAttribute}
                                    disabled={task.isCompleted}
                                    isROPackageType={isShotSelectorDisabled}
                                />
                                {!task.isCompleted && !isVOUpload && <MultiFileUpload uploadImages={false} />}
                            </div>
                            <div className="column -span9">
                                {costcoFeatureName && (
                                    <NotificationBar
                                        className="m-l-32"
                                        variant="small"
                                        type="info"
                                    >
                                        {costcoFeatureName}
                                    </NotificationBar>
                                )}
                                <div className="photo-utility-nav">
                                    <div className="flex-column">{!task.isCompleted && !isVOSourceEdit && <BulkSelect />}</div>
                                    <div className="flex">
                                        <MeasurementsDetails className="m-b-12" />
                                        {imperialWidth && isDigital && !task.context.isFinalized && !task.isCompleted && (
                                            <button
                                                className="button -primary -no-margin"
                                                onClick={handleEditMeasurements}
                                            >
                                                Edit
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <ShotLocales
                                    isFinalized={task.context.isFinalized}
                                    isCompleted={task.isCompleted}
                                    showEcom={!task.context.isSampleUpc}
                                    taskId={taskKey}
                                    isVOUpload={isVOUpload}
                                    isVODelete={isVODelete}
                                    isVOSourceEdit={isVOSourceEdit}
                                />
                            </div>
                        </div>
                    </PhotographyShots>
                </div>
            </div>
        </PhotographyLookups>
    );
};

export default PhotographyIndex;
